/**
 * Angular Add-On Type Selector Component
 *
 * Migrated version of the AngularJS acme-addon-type-selector component.
 * Original: apps/b2b-ui/common/components/addon-type-selector/addon-type-selector.component.ts
 *
 * Thin wrapper around the shared `acme-combobox` component: owns the add-on
 * data shape (Inputs/Outputs, computedLabel/uniqueName) plus the per-row
 * template projected into the combobox. Mirrors `app-ticket-type-selector` but
 * with the add-on typeahead row (no discounted price).
 *
 * Usage in Angular templates:
 *   <app-addon-type-selector
 *     [items]="event.moreAddOns"
 *     [eventId]="event.eventId"
 *     (select)="addInventoryType($event.selectedItem)">
 *   </app-addon-type-selector>
 *
 * Usage in AngularJS templates (after downgrade — element name preserved):
 *   <acme-addon-type-selector
 *     event-id="event.eventId"
 *     [items]="event.$meta.moreAddOns"
 *     on-select="ordersEdit.addInventoryTypeForEvent($event.selectedItem, event)">
 *   </acme-addon-type-selector>
 */
import { Component, EventEmitter, HostBinding, Input, Output, computed, signal } from '@angular/core';
import { CurrencyPipe, NgIf } from '@angular/common';

import { ComboboxComponent } from '../combobox/combobox.component';
import type { TODO } from '@b2b/types/core';

/**
 * Represents an add-on/inventory type item that can be selected.
 * Based on the structure returned by getRemainingAddOnsForEvent.
 */
export interface AddonTypeItem {
  id: string;
  name: string;
  description?: string;
  price: string | number | null;
  csrDisabled?: boolean;
  salesChannels?: TODO[];
}

export interface AddonTypeSelectorSelectEvent {
  selectedItem: AddonTypeItem;
}

@Component({
  selector: 'app-addon-type-selector',
  standalone: true,
  imports: [NgIf, CurrencyPipe, ComboboxComponent],
  template: require('./addon-type-selector.component.html'),
  styles: [require('./addon-type-selector.component.less')],
})
export class AddonTypeSelectorComponent {
  private readonly _items = signal<AddonTypeItem[]>([]);
  @Input()
  get items(): AddonTypeItem[] { return this._items(); }
  set items(value: AddonTypeItem[]) { this._items.set(value); }

  private readonly _eventId = signal('');
  @Input()
  get eventId(): string { return this._eventId(); }
  set eventId(value: string) { this._eventId.set(value); }

  private readonly _label = signal('');
  @Input()
  get label(): string { return this._label(); }
  set label(value: string) { this._label.set(value); }

  @Input() disabled = false;
  /** When true (default), typing in the input filters items by `name` (case-insensitive). */
  @Input() filterable = true;

  @Output() select = new EventEmitter<AddonTypeSelectorSelectEvent>();

  @HostBinding('class.acme-addon-type-selector') readonly baseClass = true;

  readonly computedLabel = computed(() => {
    if (this._label()) return this._label();
    const count = this._items()?.length ?? 0;
    return count > 0 ? `Add Add-On (${count} more)` : 'Add Add-On';
  });

  readonly uniqueName = computed(() => `addon-type-selector-${this._eventId()}`);

  onSelectionChange(item: AddonTypeItem): void {
    this.select.emit({ selectedItem: item });
  }
}
